// src/pages/admin/AdminNotifications.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  FiBell, FiCheck, FiTrash2, FiSearch,
  FiAlertCircle, FiInfo, FiCheckCircle, FiShoppingBag,
  FiUsers, FiPackage, FiDollarSign, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/api';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); 
  const [typeFilter, setTypeFilter] = useState('all'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const normalizeNotification = (n) => ({
    _id: n._id || n.id || `${Date.now()}-${Math.random()}`,
    type: n.type || 'info',
    title: n.title || n.message || '',
    message: n.message || '',
    isRead: typeof n.read === 'boolean' ? n.read : !!n.isRead,
    createdAt: n.createdAt || n.created_at || new Date().toISOString(),
    icon: n.icon || 'FiInfo',
    color: n.color || 'gray',
    raw: n
  });

  const fetchNotifications = useCallback(async () => {
    if (mountedRef.current) setLoading(true);
    try {
      if (!adminAPI || !adminAPI.getNotifications) {
        throw new Error('adminAPI.getNotifications not implemented');
      }

      const res = await adminAPI.getNotifications();
      const data = res?.data ?? res;

      let arr = [];
      if (data?.success && Array.isArray(data.data)) arr = data.data;
      else if (Array.isArray(data)) arr = data;
      else if (Array.isArray(data?.data)) arr = data.data;
      else arr = [];

      const mapped = arr.map(normalizeNotification);
      if (mountedRef.current) setNotifications(mapped);

      if (mountedRef.current && mapped.length === 0) {
        toast.info('Aucune notification réelle trouvée.');
      }

      return mapped;
    } catch (err) {
      console.error('fetchNotifications error', err);
      if (mountedRef.current) {
        setNotifications([]); 
        toast.error('Impossible de récupérer les notifications réelles depuis le backend.');
      }
      return [];
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (id) => {
    if (!id) return;
    try {
      if (adminAPI && adminAPI.markAsRead) {
        const res = await adminAPI.markAsRead(id);
        if (res?.data?.data) {
          const updated = normalizeNotification(res.data.data);
          setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: updated.isRead } : n));
        } else {
          setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
        }
      } else {
        setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      }
      toast.success('Notification marquée comme lue');
    } catch (err) {
      console.error('handleMarkAsRead error', err);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      toast.success('Notification marquée comme lue (local)');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (adminAPI && adminAPI.markAllAsRead) {
        await adminAPI.markAllAsRead();
      } else if (adminAPI && adminAPI.markAsRead) {
        const unread = notifications.filter(n => !n.isRead).map(n => n._id);
        await Promise.all(unread.map(id => adminAPI.markAsRead(id).catch(() => {})));
      } else {
        throw new Error('no-mark-all-endpoint');
      }
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (err) {
      console.error('handleMarkAllAsRead error', err);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      toast.success('Toutes les notifications marquées comme lues (local)');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm('Supprimer cette notification?')) return;
    try {
      if (adminAPI && adminAPI.deleteNotification) {
        await adminAPI.deleteNotification(id);
      } else {
        throw new Error('no-delete-endpoint');
      }
      setNotifications(prev => prev.filter(n => n._id !== id));
      toast.success('Notification supprimée');
    } catch (err) {
      console.error('handleDelete error', err);
      setNotifications(prev => prev.filter(n => n._id !== id));
      toast.success('Notification supprimée (local)');
    }
  };

  const handleDeleteAll = async () => {
    if (isSubmitting) return;
    if (!window.confirm('Supprimer toutes les notifications lues?')) return;
    setIsSubmitting(true);
    try {
      if (adminAPI && adminAPI.deleteReadNotifications) {
        await adminAPI.deleteReadNotifications();
      } else if (adminAPI && adminAPI.deleteNotification) {
        const readIds = notifications.filter(n => n.isRead).map(n => n._id);
        await Promise.all(readIds.map(id => adminAPI.deleteNotification(id).catch(() => {})));
      } else {
        throw new Error('no-delete-all-endpoint');
      }
      setNotifications(prev => prev.filter(n => !n.isRead));
      toast.success('Notifications supprimées');
    } catch (err) {
      console.error('handleDeleteAll error', err);
      setNotifications(prev => prev.filter(n => !n.isRead));
      toast.success('Notifications supprimées (local)');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIconComponent = (iconName, color) => {
    const iconMap = {
      FiShoppingBag,
      FiUsers,
      FiPackage,
      FiDollarSign,
      FiAlertCircle,
      FiInfo,
      FiCheckCircle,
      FiBell
    };
    const IconComponent = iconMap[iconName] || FiBell;
    const colorMap = {
      blue: 'text-blue-500 bg-blue-100',
      green: 'text-green-500 bg-green-100',
      red: 'text-red-500 bg-red-100',
      purple: 'text-purple-500 bg-purple-100',
      yellow: 'text-yellow-600 bg-yellow-100'
    };
    return (
      <div className={`p-3 rounded-lg ${colorMap[color] || 'text-gray-500 bg-gray-100'}`}>
        <IconComponent size={24} />
      </div>
    );
  };

  const safeLower = (s) => String(s || '').toLowerCase();

  const filteredNotifications = useMemo(() => {
    return notifications.filter(notif => {
      const matchesReadFilter =
        filter === 'all' ? true :
        filter === 'unread' ? !notif.isRead :
        notif.isRead;

      const matchesTypeFilter = typeFilter === 'all' || notif.type === typeFilter;

      const term = safeLower(searchTerm).trim();
      const matchesSearch = !term || safeLower(notif.title).includes(term) || safeLower(notif.message).includes(term);

      return matchesReadFilter && matchesTypeFilter && matchesSearch;
    });
  }, [notifications, filter, typeFilter, searchTerm]);


  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const total = filteredNotifications.length;
  const totalPages = Math.max(1, Math.ceil(total / (limit || 1)));

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filter, typeFilter, limit]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  const paginatedNotifications = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredNotifications.slice(start, start + limit);
  }, [filteredNotifications, page, limit]);
  /* --------------------------- */

  const stats = useMemo(() => {
    const totalN = notifications.length;
    const unread = notifications.filter(n => !n.isRead).length;
    const read = notifications.filter(n => n.isRead).length;
    const today = notifications.filter(n => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const notifDate = new Date(n.createdAt || n.raw?.createdAt || new Date().toISOString());
      notifDate.setHours(0, 0, 0, 0);
      return today.getTime() === notifDate.getTime();
    }).length;
    return { total: totalN, unread, read, today };
  }, [notifications]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
    
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-600 mt-1">Gérez vos notifications système (sources réelles uniquement)</p>
      </div>

     
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FiBell className="text-gray-400 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Non Lues</p>
              <p className="text-2xl font-bold text-blue-600">{stats.unread}</p>
            </div>
            <FiAlertCircle className="text-blue-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Lues</p>
              <p className="text-2xl font-bold text-green-600">{stats.read}</p>
            </div>
            <FiCheckCircle className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aujourd'hui</p>
              <p className="text-2xl font-bold text-purple-600">{stats.today}</p>
            </div>
            <FiInfo className="text-purple-500 text-2xl" />
          </div>
        </div>
      </div>

     
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Toutes</option>
            <option value="unread">Non lues</option>
            <option value="read">Lues</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Tous types</option>
            <option value="order">Commandes</option>
            <option value="user">Utilisateurs</option>
            <option value="deal">Deals</option>
            <option value="payment">Paiements</option>
          </select>

          <div className="flex space-x-2">
            <button
              onClick={handleMarkAllAsRead}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
            >
              Tout lire
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
            >
              Supprimer lues
            </button>
          </div>
        </div>
      </div>

     
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {paginatedNotifications.length > 0 ? (
            paginatedNotifications.map((notif) => (
              <div
                key={notif._id}
                className={`p-4 hover:bg-gray-50 transition-colors ${!notif.isRead ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start space-x-4">
                  {getIconComponent(notif.icon, notif.color)}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-sm font-semibold text-gray-900">{notif.title || ''}</h3>
                          {!notif.isRead && <span className="w-2 h-2 bg-blue-600 rounded-full" aria-hidden="true"></span>}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{notif.message || ''}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notif.createdAt || new Date().toISOString()).toLocaleString('fr-FR')}
                        </p>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {!notif.isRead && (
                          <button
                            onClick={() => handleMarkAsRead(notif._id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Marquer comme lu"
                            type="button"
                          >
                            <FiCheck size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(notif._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Supprimer"
                          type="button"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <FiBell className="mx-auto text-4xl text-gray-400 mb-2" />
              <p className="text-gray-600">Aucune notification</p>
            </div>
          )}
        </div>

      
        <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
          <div className="flex items-center space-x-2">
            <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1 || loading} className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1">
              <FiChevronLeft /> <span>Préc</span>
            </button>
            <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= totalPages || loading} className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1">
              <span>Suiv</span> <FiChevronRight />
            </button>

            <div className="ml-4 text-sm text-gray-600">Page <strong>{page}</strong> / <strong>{totalPages}</strong></div>

            <div className="ml-4 text-sm text-gray-600">
              <span className="mr-2">Afficher</span>
              <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="border px-2 py-1 rounded">
                {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="ml-2">/ page</span>
            </div>
          </div>

          <div className="text-sm text-gray-600">{total > 0 ? `Total: ${total} résultats` : `${filteredNotifications.length} affichés`}</div>
        </div>
      </div>

  
    </div>
  );
};

export default AdminNotifications;
